import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {AccountLink, UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {getBulkResult, getRemoveRelationsProgress} from 'src/openapi/admin/apis/AccountManagerRemoveBulkApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group, Item} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {pluralize} from '@pb/kit-formatters';

import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {ProcessedRemovedRelationItem} from 'src/openapi/admin/models/ProcessedRemovedRelationItem';

type Props = {
    id: string,
    onClose: () => void,
};

export const RemoveAccountTeamMemberResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();

    const getPaginatedResults = useCallback(
        (bulkResult: ProcessedRemovedRelationItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: ProcessedRemovedRelationItem, index) => {
                const accountLink = (
                    <AccountLink
                        accountId={res.accountId || 0}
                        accountName={res.accountName}
                        blank={true}
                        place={`${TOOL_TITLE} Result`}
                    />
                );

                return (
                    <Row key={index}>
                        <Cell width={50}>
                            <RowNumbered
                                maxRowNumber={bulkResult?.length || 0}
                                placeholder={(currentPage - 1) * pageSize + index + 1}
                            />
                        </Cell>
                        <Cell>{accountLink}</Cell>
                        <Cell>
                            <CompleteMark errors={res.errors} tooltip={res.affectedRelations}/>
                        </Cell>
                    </Row>
                );
            }),
        [],
    );

    const getLink = useCallback((itemList: Array<ProcessedRemovedRelationItem>) => (
        <UserLink
            blank={true}
            place={`${TOOL_TITLE} Result`}
            userId={itemList[0].managerId}
            caption={itemList[0].managerEmail}
        />
    ), []);

    return (
        <BulkActionResultModal<ProcessedRemovedRelationItem>
            id={id}
            onClose={onClose}
            width={1200}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between" alignItems="center">
                    {bulkResult && bulkResult.itemList && bulkResult.itemList.length > 0 ? (
                        <Item>
                            Result of the unassignment of {getLink(bulkResult.itemList)} from {
                                pluralize('account[s]', bulkResult?.itemList?.length)
                            }
                        </Item>
                    ) : null}
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult?.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            accountId: item.accountId.toString(),
                            accountName: item.accountName,
                            managerEmail: item.managerEmail,
                            managerId: item.managerId.toString(),
                            errors: item.errors?.join('. '),
                            actionStatus: item.actionStatus,
                            affectedRelations: item.affectedRelations?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(result, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={56}><strong>#</strong></Cell>
                        <Cell>Account</Cell>
                        <Cell>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(result, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getRemoveRelationsProgress({id}))}
            getResult={() => apiDispatch(getBulkResult({id}))}

            sortComparator={(a, b) => a.accountName.localeCompare(b.accountName)}
        />
    );
};
